import React, { useCallback, useContext, useState } from 'react';
import { Modal, notification, Button, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { MessageOutlined, SendOutlined } from '@ant-design/icons';

import { goToApp } from "utils/helpers";
import { AppContext } from 'context/AppContext';
import Download from './Download';
import CommentDrawer from '../../Comment/CommentDrawer';
import PostItem from 'components/PostItem';
import '../TaskDetail.css';

const { TextArea } = Input;

// Comment Input Component
const CommentInput = ({
  visible,
  onSubmit,
  onCancel,
  placeholder,
  isSubmitting = false
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  const handleSubmit = useCallback(async () => {
    if (!value.trim()) return;
    try {
      await onSubmit(value.trim());
      setValue('');
    } catch (error) {
      // Error handling is done in parent component
    }
  }, [value, onSubmit]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  if (!visible) return null;

  return (
    <div className="comment-input-container">
      <div className="comment-input-wrapper">
        <TextArea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          autoSize={{ minRows: 1, maxRows: 4 }}
          onKeyPress={handleKeyPress}
          disabled={isSubmitting}
          className="comment-input"
        />
        <div className="comment-input-actions">
          <Button
            type="text"
            size="small"
            onClick={onCancel}
            disabled={isSubmitting}
            className="comment-cancel-btn"
          >
            {t('#Comment.cancel')}
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={handleSubmit}
            disabled={!value.trim() || isSubmitting}
            loading={isSubmitting}
            icon={!isSubmitting && <SendOutlined />}
            className="comment-send-btn"
          >
            {t('#Comment.send')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const SubmissionItem = ({
  item,
  onLikeUpdate,
  isInCommentDrawer = false,
  onCommentAdd,
  showCommentInput = true,
  ...props
}) => {
  const { t } = useTranslation();
  const { api, authentication } = useContext(AppContext);
  const { authKey } = authentication;
  const [modal2Visible, setModal2Visible] = useState(false);
  const [commentDrawerVisible, setCommentDrawerVisible] = useState(false);
  const [showCommentInputState, setShowCommentInputState] = useState(true);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const handleGoToApp = useCallback(() => {
    setModal2Visible(true);
    setTimeout(() => goToApp(false), 3000);
  }, []);

  const handleCommentClick = useCallback(() => {
    if (isInCommentDrawer) {
      // In comment drawer, toggle the comment input
      if (!authKey) {
        handleGoToApp();
        return;
      }
      setShowCommentInputState(!showCommentInputState);
      return;
    }

    if (!authKey) {
      handleGoToApp();
      return;
    }

    setCommentDrawerVisible(true);
  }, [authKey, handleGoToApp, isInCommentDrawer, showCommentInputState]);

  const handleCommentSubmit = useCallback(async (message) => {
    setIsSubmittingComment(true);

    try {
      const submissionURL = `${process.env.REACT_APP_DOMAIN}/api/submissions/${item.id}/`;
      const result = await api.comment.submitComment({
        object: submissionURL,
        message: message
      });

      if (result.success) {
        notification.success({
          message: t(''),
          description: t('#Comment.comment_added_successfully'),
        });

        setShowCommentInputState(false);

        // Update comment count in submission
        if (onLikeUpdate) {
          onLikeUpdate({
            ...item,
            comment_count: item.comment_count + 1
          });
        }

        // Notify parent component about new comment
        if (onCommentAdd && result.data) {
          onCommentAdd(result.data);
        }
      } else {
        notification.error({
          message: t(''),
          description: t('#Comment.error.submit_error'),
        });
      }
    } catch (error) {
      notification.error({
        message: t(''),
        description: t('#Comment.error.submit_error'),
      });
    } finally {
      setIsSubmittingComment(false);
    }
  }, [item, api.comment, onLikeUpdate, onCommentAdd, t]);

  const handleCommentCancel = useCallback(() => {
    setShowCommentInputState(false);
  }, []);

  // Comment section for in-drawer mode
  const commentSection = isInCommentDrawer && showCommentInput ? (
    <div className="submission-comment-section">
      <div className="submission-comment-header">
        <Button
          type="text"
          icon={<MessageOutlined />}
          onClick={handleCommentClick}
          className={`submission-comment-toggle ${showCommentInput ? 'active' : ''}`}
        >
          {showCommentInputState ? t('#Comment.cancel') : t('#Comment.add_comment')}
        </Button>
      </div>

      <CommentInput
        visible={showCommentInputState}
        onSubmit={handleCommentSubmit}
        onCancel={handleCommentCancel}
        placeholder={t('#Comment.write_reply')}
        isSubmitting={isSubmittingComment}
      />
    </div>
  ) : null;

  return (
    <>
      <PostItem
        id={item.id}
        message={item.message}
        images={item.images}
        user={item.user}
        created={item.created}
        is_liked={item.is_liked}
        like_count={item.like_count}
        comment_count={item.comment_count}
        onLikeUpdate={onLikeUpdate}
        onCommentClick={handleCommentClick}
        showFollow={false}
        showCommentButton={!isInCommentDrawer}
        afterActions={commentSection}
        className={`submission-item ${isInCommentDrawer ? 'in-comment-drawer' : ''}`}
      />

      {/* Download App Modal */}
      <Modal
        centered
        open={modal2Visible}
        footer={null}
        width={720}
        onCancel={() => setModal2Visible(false)}
        style={{ borderRadius: 'var(--radius-lg)' }}
      >
        <Download />
      </Modal>

      {/* Comment Drawer - Only when NOT already in comment drawer */}
      {!isInCommentDrawer && (
        <CommentDrawer
          isOpen={commentDrawerVisible}
          closeHandler={() => setCommentDrawerVisible(false)}
          submission={item}
          onLikeUpdate={onLikeUpdate}
        />
      )}
    </>
  );
};

export default SubmissionItem;